import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import './AdminLayout.css';

interface MenuItem {
  path: string;
  icon: string;
  label: string;
}

interface MenuGroup {
  icon: string;
  label: string;
  children: MenuItem[];
}

type SidebarItem = MenuItem | MenuGroup;

function isMenuGroup(item: SidebarItem): item is MenuGroup {
  return 'children' in item;
}

const AdminLayout: React.FC = () => {
  const { i18n } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const sidebarItems: SidebarItem[] = [
    { path: '/admin/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/admin/categories', icon: '📁', label: 'Categories' },
    { path: '/admin/products', icon: '📦', label: 'Products' },
    { path: '/admin/orders', icon: '🛍️', label: 'Orders' },
    { path: '/admin/customers', icon: '👥', label: 'Customers' },
    { path: '/admin/reviews', icon: '⭐', label: 'Reviews' },
    { path: '/admin/coupons', icon: '🎫', label: 'Coupons' },
    { path: '/admin/banners', icon: '🖼️', label: 'Banners' },
    { path: '/admin/delivery-options', icon: '🚚', label: 'Delivery Options' },
    { path: '/admin/content', icon: '📝', label: 'Content' },
    {
      icon: '🖥️', label: 'CMS',
      children: [
        { path: '/admin/cms/home', icon: '🏠', label: 'Home Page' },
        { path: '/admin/cms/about', icon: '📄', label: 'About Us' },
        { path: '/admin/cms/contact', icon: '📞', label: 'Contact' },
        { path: '/admin/cms/faq', icon: '❓', label: 'FAQ' },
        { path: '/admin/cms/shipping', icon: '🚚', label: 'Shipping Policy' },
        { path: '/admin/cms/returns', icon: '🔄', label: 'Returns' },
        { path: '/admin/cms/terms', icon: '📜', label: 'Terms' },
        { path: '/admin/cms/privacy', icon: '🔒', label: 'Privacy Policy' },
      ],
    },
    { path: '/admin/settings', icon: '🎨', label: 'Website Settings' },
    { path: '/admin/integrations', icon: '🔌', label: 'Integrations' },
    {
      icon: '📢', label: 'Meta Ads',
      children: [
        { path: '/admin/pixel', icon: '📍', label: 'Facebook Pixel' },
        { path: '/admin/capi', icon: '🔄', label: 'Facebook CAPI' },
        { path: '/admin/meta-ads/config', icon: '📡', label: 'Ads Config' },
        { path: '/admin/meta-ads/report', icon: '📈', label: 'Ads Report' },
        { path: '/admin/meta-ads/products', icon: '🔗', label: 'Campaign Products' },
      ],
    },
    { path: '/admin/gtm', icon: '🏷️', label: 'Google Tag Manager' },
    { path: '/admin/translations', icon: '🌐', label: 'Translations' },
    { path: '/admin/profile', icon: '👤', label: 'My Profile' },
  ];

  // Check if a group has an active child
  const isGroupActive = (group: MenuGroup) =>
    group.children.some((child) => location.pathname.startsWith(child.path));

  // Auto-expand groups that contain the active route
  const isGroupOpen = (label: string, group: MenuGroup) =>
    openMenus[label] !== undefined ? openMenus[label] : isGroupActive(group);

  const toggleGroup = (label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // Resolve current page title for the header
  const getPageTitle = (): string => {
    for (const item of sidebarItems) {
      if (isMenuGroup(item)) {
        const match = item.children.find((c) => location.pathname.startsWith(c.path));
        if (match) return `${item.label} › ${match.label}`;
      } else if (location.pathname.startsWith(item.path)) {
        return item.label;
      }
    }
    return 'Admin';
  };

  return (
    <div className={`admin-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            E-Store Admin
          </Link>
          <button
            className="toggle-sidebar-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? '❮' : '❯'}
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul>
            {sidebarItems.map((item) =>
              isMenuGroup(item) ? (
                <li key={item.label} className="nav-group">
                  <button
                    className={`nav-item nav-group-header ${isGroupActive(item) ? 'active' : ''}`}
                    onClick={() => toggleGroup(item.label)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {sidebarOpen && <span className="nav-label">{item.label}</span>}
                    {sidebarOpen && (
                      <span className={`nav-group-chevron ${isGroupOpen(item.label, item) ? 'open' : ''}`}>
                        ▸
                      </span>
                    )}
                  </button>
                  {sidebarOpen && isGroupOpen(item.label, item) && (
                    <ul className="nav-submenu">
                      {item.children.map((child) => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            className={`nav-item nav-subitem ${location.pathname.startsWith(child.path) ? 'active' : ''}`}
                          >
                            <span className="nav-icon">{child.icon}</span>
                            <span className="nav-label">{child.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ) : (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`nav-item ${location.pathname.startsWith(item.path) ? 'active' : ''}`}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {sidebarOpen && <span className="nav-label">{item.label}</span>}
                  </Link>
                </li>
              )
            )}
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <div className="header-left">
            <h2 className="page-title">
              {getPageTitle()}
            </h2>
          </div>
          <div className="header-right">
            <button
              className="refresh-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('admin-refresh'))}
              title="Refresh page data"
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                background: 'white',
                color: '#6b7280',
                fontSize: '0.8rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#0284c7';
                e.currentTarget.style.color = '#0284c7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              🔄
            </button>
            <button
              className="lang-toggle"
              onClick={() => i18n.changeLanguage(i18n.language === 'bn' ? 'en' : 'bn')}
            >
              {i18n.language === 'bn' ? 'EN' : 'BN'}
            </button>
            <Link to="/" className="view-site-btn">
              View Site ↗
            </Link>
            <div className="header-user-info">
              <span>Welcome, {user?.first_name || user?.username || 'Admin'}</span>
            </div>
            <button onClick={handleLogout} className="header-logout-btn">
              Logout
            </button>
          </div>
        </header>

        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;

